import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** Verhindert doppelte async-section:loaded-Listener bei schnellen Mehrfach-Opens. */
  #trailPending = false;

  /** Muss dem CSS-Breakpoint entsprechen, der den Drawer versteckt (header.css: min-width 1200px). */
  #desktopMq = window.matchMedia('(min-width: 1200px)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // capture phase: 'toggle' does not bubble
    this.addEventListener('toggle', this.#onToggle, true);
    this.#desktopMq.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onToggle, true);
    this.#desktopMq.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Offener Drawer + Resize auf Desktop: CSS blendet den Drawer nur aus
   * (display:none), das <details> bliebe offen — Scroll-Lock (html[scroll-lock]
   * via dialog.js-Toggle-Listener) und Fokus-Falle würden als Zombies
   * weiterleben. close() hilft hier nicht: es wartet auf das transitionend des
   * Panels, das bei display:none nie feuert. Deshalb: harter Reset ohne
   * Animation. Das Entfernen von [open] feuert `toggle` → dialog.js räumt das
   * html[scroll-lock]-Attribut ab.
   * @param {MediaQueryListEvent} event
   */
  #onBreakpointChange = (event) => {
    if (!event.matches || !this.isOpen) return;
    removeTrapFocus();
    const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
    openDetails.forEach(reset);
  };

  /**
   * Drive the slide-in class from the native `toggle` event — the earliest
   * reliable "content is now rendered" signal, for the root drawer and submenu
   * drill-ins alike (native summary activation sets `open` AFTER click
   * handlers). The double rAF guarantees the off-screen state gets painted
   * before the class flips the transform: adding the class in the same frame
   * as the `open` attribute skipped the CSS transition intermittently (drawer
   * and submenus popped in instead of sliding).
   * @param {Event} event
   */
  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details.closest('accordion-custom')) return;
    if (!details.open) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (details.open) details.classList.add('menu-open');
      });
    });
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Lazy-Nav-Content laden falls noch nicht geladen.
    // async-section selbst no-op't wenn schon hydrated oder gerade lädt.
    const asyncSection = details.querySelector('async-section[data-priority="manual"]');
    if (asyncSection && typeof asyncSection.load === 'function') {
      asyncSection.load();
    }

    // Active-Trail: bei JEDEM Öffnen des Root-Drawers direkt auf der Ebene der
    // aktuellen Seite einsteigen (Pathname-Match = clientseitiges link.current —
    // serverseitig unmöglich, weil das Menü von der festen Route "/" gefetcht
    // und der Response seitenübergreifend gecacht wird). Idempotent; Schließen
    // resettet ohnehin alle Ebenen, ein Re-Open startet also immer sauber —
    // nur einmal-pro-Pageload ließ jedes zweite Öffnen auf Root landen und
    // versteckte den Active-Link in der zugeklappten Ebene.
    if (details === this.refs.details) {
      if (asyncSection?.dataset.state === 'loaded' || !asyncSection) {
        this.#applyActiveTrail();
      } else if (!this.#trailPending) {
        this.#trailPending = true;
        asyncSection.addEventListener(
          'async-section:loaded',
          () => {
            this.#trailPending = false;
            if (this.isOpen) this.#applyActiveTrail();
          },
          { once: true }
        );
      }
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    // 'menu-open' is added by #onToggle (double rAF) so the slide-in always
    // starts from a painted off-screen frame.
    requestAnimationFrame(() => {
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Öffnet beim ersten Drawer-Open die <details>-Kette des Menü-Links, der auf
   * die aktuelle Seite zeigt (Schmuck → Frauen → Ringe ⇒ Drawer startet auf der
   * Frauen-Ebene). Ohne Slide-Animation (.header-drawer--instant), damit die
   * Panels nicht kaskadieren. Kein Treffer ⇒ Root-Verhalten wie bisher.
   */
  #applyActiveTrail() {
    const nav = this.querySelector('.menu-drawer__navigation');
    if (!nav) return;

    const normalize = (/** @type {string} */ p) => p.replace(/\/$/, '') || '/';
    const here = normalize(location.pathname);
    // Menü kommt von "/" (Primär-Locale) — auf /en/… etc. zusätzlich den
    // Locale-Präfix der aktuellen URL abstreifen.
    const stripped = normalize(here.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/, ''));

    let active = null;
    for (const anchor of nav.querySelectorAll('a[href]')) {
      const path = normalize(new URL(anchor.getAttribute('href') ?? '', location.origin).pathname);
      if (path === '/') continue; // Logo-/Home-Links matchen sonst überall
      if (path === here || path === stripped) {
        active = anchor;
        break;
      }
    }
    if (!active) return;

    active.classList.add('menu-drawer__menu-item--active');

    /** @type {HTMLDetailsElement[]} */
    const chain = [];
    let node = active.closest('details');
    while (node instanceof HTMLDetailsElement && node !== this.refs.details) {
      chain.unshift(node);
      node = node.parentElement?.closest('details') ?? null;
    }
    if (chain.length === 0) return;

    this.classList.add('header-drawer--instant');
    for (const det of chain) {
      det.open = true;
      det.classList.add('menu-open');
      det.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('header-drawer--instant'));
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait only for the panel's own slide-out transition (subtree animations —
    // the nav item stagger — would delay the reset, and with it the removal of
    // the [open] attribute that keeps the content rendered during the slide).
    const panel = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu') ?? details;
    onAnimationEnd(panel, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    }, { subtree: false });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
